import React, { useEffect, useState } from "react";
import {
  Box, Button, Checkbox, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Typography, Paper, Dialog, DialogTitle,
  DialogContent, DialogActions
} from "@mui/material";

import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

import { db } from "../firebase_setup/firebase";
import { collection, addDoc, updateDoc, doc, onSnapshot } from "firebase/firestore";

export default function InventoryDashboard() {
  const [products, setProducts] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [newProduct, setNewProduct] = useState({
    name: "", package: "", stock: 0, minStock: 0,
    purchasePrice: 0, sellPrice: 0, order: false, orderQty: 0
  });

  // Helper: форматиране в евро
  const toEuro = price => {
    const num = parseFloat(price);
    if (isNaN(num)) return "0.00 €";
    return num.toFixed(2) + " €";
  };

  const vatPrice = price => {
    const num = parseFloat(price);
    if (isNaN(num)) return "0.00 €";
    return (num * 1.2).toFixed(2) + " €";
  };

  const margin = (sell, purchase) => {
    const s = parseFloat(sell), p = parseFloat(purchase);
    if (isNaN(s) || isNaN(p) || s === 0) return "0%";
    return (((s - p)/s)*100).toFixed(1) + "%";
  };

  // Добавяне на продукт
  const addProduct = async () => {
    if (!db) return console.error("db е undefined!");
    try {
      const productsCol = collection(db, "products");
      await addDoc(productsCol, newProduct);
      setOpenDialog(false);
      setNewProduct({ name: "", package: "", stock: 0, minStock: 0, purchasePrice: 0, sellPrice: 0, order: false, orderQty: 0 });
    } catch (err) {
      console.error("Грешка при добавяне на продукт:", err);
    }
  };

  // Update продукт
  const saveProduct = async (id) => {
    if (!db) return;
    const p = products.find(x => x.id === id);
    if (!p) return;
    const docRef = doc(db, "products", id);
    try {
      await updateDoc(docRef, {
        name: p.name,
        package: p.package,
        stock: p.stock,
        minStock: p.minStock,
        purchasePrice: p.purchasePrice,
        sellPrice: p.sellPrice
      });
      setProducts(prev => prev.map(x => x.id === id ? { ...x, isEditing: false } : x));
    } catch(err) {
      console.error("Грешка при запис на продукта:", err);
    }
  };

  const enableEdit = (id) => setProducts(prev => prev.map(p => p.id === id ? { ...p, isEditing: true } : p));
  const cancelEdit = (id) => {
    if (!db) return;
    const productsCol = collection(db, "products");
    onSnapshot(productsCol, snapshot => {
      const prods = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setProducts(prods);
    });
  };

  const updateProductLocal = (id, field, value) => {
    setProducts(prev => prev.map(p => p.id === id ? { ...p, [field]: value } : p));
  };

  const toggleOrder = async (id, value) => {
    if (!db) return;
    try {
      const docRef = doc(db, "products", id);
      await updateDoc(docRef, { order: value });
      setProducts(prev => prev.map(p => p.id === id ? { ...p, order: value } : p));
    } catch (err) {
      console.error("Грешка при обновяване на поръчка:", err);
    }
  };

  const updateOrderQty = async (id, qty) => {
    if (!db) return;
    try {
      const docRef = doc(db, "products", id);
      await updateDoc(docRef, { orderQty: qty });
      setProducts(prev => prev.map(p => p.id === id ? { ...p, orderQty: qty } : p));
    } catch(err) {
      console.error("Грешка при update на orderQty:", err);
    }
  };

  // Load products
  useEffect(() => {
    if (!db) return;
    const productsCol = collection(db, "products");
    const unsubscribe = onSnapshot(productsCol, snapshot => {
      const prods = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setProducts(prods);
    });
    return () => unsubscribe();
  }, []);

  // Генериране PDF с кирилица
  const generatePDF = () => {
    const doc = new jsPDF();

    // Зареждаме шрифт DejaVuSans за кирилица
    doc.addFileToVFS("DejaVuSans.ttf", "<BASE64_FONT_STRING_HERE>");
    doc.addFont("DejaVuSans.ttf", "DejaVuSans", "normal");
    doc.setFont("DejaVuSans");

    const tableData = products
      .filter(p => p.order && p.orderQty > 0)
      .map(p => [
        p.name,
        p.orderQty,
        toEuro(p.purchasePrice),
        toEuro((p.purchasePrice * p.orderQty).toFixed(2))
      ]);

    autoTable(doc, {
      head: [["Продукт", "Количество", "Доставна", "Общо"]],
      body: tableData,
      startY: 30
    });

    const totalOrder = products
      .filter(p => p.order && p.orderQty > 0)
      .reduce((sum, p) => sum + (p.orderQty * parseFloat(p.purchasePrice)), 0);

    doc.text(`Обща стойност: ${toEuro(totalOrder)}`, 14, doc.lastAutoTable?.finalY + 10 || 10);
    doc.save("PurchaseOrder.pdf");
  };

  return (
    <Box sx={{p:2}}>
      <Typography variant="h4" gutterBottom>Склад / Наличности</Typography>

      <Button variant="contained" onClick={()=>setOpenDialog(true)} sx={{mb:2}}>Добави продукт</Button>
      <Button variant="outlined" onClick={generatePDF} sx={{mb:2, ml:2}}>Генерирай PDF поръчка</Button>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Продукт</TableCell>
              <TableCell>Опаковка</TableCell>
              <TableCell>Наличност</TableCell>
              <TableCell>Мин.</TableCell>
              <TableCell>Доставна цена</TableCell>
              <TableCell>Ед. цена</TableCell>
              <TableCell>Ед. цена с ДДС</TableCell>
              <TableCell>Марж</TableCell>
              <TableCell>Поръчка</TableCell>
              <TableCell>Действие</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {products.map(p => {
              const needOrder = p.stock < (p.minStock || 0);
              const isEditing = p.isEditing || false;

              return (
                <TableRow key={p.id} sx={{ backgroundColor: needOrder ? "#ffe6e6" : "white" }}>
                  <TableCell>{isEditing ? <TextField value={p.name} onChange={e=>updateProductLocal(p.id,"name",e.target.value)} variant="standard"/> : p.name}</TableCell>
                  <TableCell>{isEditing ? <TextField value={p.package} onChange={e=>updateProductLocal(p.id,"package",e.target.value)} variant="standard"/> : p.package}</TableCell>
                  <TableCell>{isEditing ? <TextField type="number" value={p.stock} onChange={e=>updateProductLocal(p.id,"stock",Number(e.target.value))} variant="standard"/> : p.stock}</TableCell>
                  <TableCell>{isEditing ? <TextField type="number" value={p.minStock} onChange={e=>updateProductLocal(p.id,"minStock",Number(e.target.value))} variant="standard"/> : p.minStock}</TableCell>
                  <TableCell>{isEditing ? <TextField type="number" value={p.purchasePrice} onChange={e=>updateProductLocal(p.id,"purchasePrice",Number(e.target.value))} variant="standard"/> : toEuro(p.purchasePrice)}</TableCell>
                  <TableCell>{isEditing ? <TextField type="number" value={p.sellPrice} onChange={e=>updateProductLocal(p.id,"sellPrice",Number(e.target.value))} variant="standard"/> : toEuro(p.sellPrice)}</TableCell>
                  <TableCell>{toEuro(vatPrice(p.sellPrice))}</TableCell>
                  <TableCell>{margin(p.sellPrice,p.purchasePrice)}</TableCell>
                  <TableCell>
                    <Checkbox checked={p.order || false} onChange={e => toggleOrder(p.id, e.target.checked)}/>
                    {p.order && (
                      <TextField
                        type="number"
                        size="small"
                        value={p.orderQty || 0}
                        onChange={e => updateOrderQty(p.id, Number(e.target.value))}
                        sx={{width:60, ml:1}}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {isEditing ? (
                      <>
                        <Button size="small" variant="contained" onClick={()=>saveProduct(p.id)} sx={{mr:1}}>Save</Button>
                        <Button size="small" variant="outlined" onClick={()=>cancelEdit(p.id)}>Cancel</Button>
                      </>
                    ) : (
                      <Button size="small" variant="outlined" onClick={()=>enableEdit(p.id)}>Edit</Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog за нов продукт */}
      <Dialog open={openDialog} onClose={()=>setOpenDialog(false)}>
        <DialogTitle>Добавяне на продукт</DialogTitle>
        <DialogContent sx={{display:'flex', flexDirection:'column', gap:2, minWidth:300}}>
          <TextField label="Продукт" value={newProduct.name} autoFocus onChange={e=>setNewProduct({...newProduct, name:e.target.value})}/>
          <TextField label="Опаковка" value={newProduct.package} onChange={e=>setNewProduct({...newProduct, package:e.target.value})}/>
          <TextField label="Наличност" type="number" value={newProduct.stock} onChange={e=>setNewProduct({...newProduct, stock:Number(e.target.value)})}/>
          <TextField label="Мин. наличност" type="number" value={newProduct.minStock} onChange={e=>setNewProduct({...newProduct, minStock:Number(e.target.value)})}/>
          <TextField label="Доставна цена" type="number" value={newProduct.purchasePrice} onChange={e=>setNewProduct({...newProduct, purchasePrice:Number(e.target.value)})}/>
          <TextField label="Продажна цена" type="number" value={newProduct.sellPrice} onChange={e=>setNewProduct({...newProduct, sellPrice:Number(e.target.value)})}/>
          <Box sx={{display:'flex', alignItems:'center', gap:1}}>
            <Checkbox checked={newProduct.order} onChange={e=>setNewProduct({...newProduct, order:e.target.checked})}/>
            <Typography>За поръчка</Typography>
            {newProduct.order && (
              <TextField
                type="number"
                size="small"
                value={newProduct.orderQty || 0}
                onChange={e=>setNewProduct({...newProduct, orderQty: Number(e.target.value)})}
                sx={{width:60}}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={()=>setOpenDialog(false)}>Отказ</Button>
          <Button variant="contained" onClick={addProduct}>Добави</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}